import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  RotateCcw, MessageCircle, Lock, LogOut, CalendarDays, Gauge, ShieldCheck, Loader2, Bell, BellOff, Globe2, Sparkles, LifeBuoy, Settings, Receipt, Zap, MonitorSmartphone, Clock, ChevronDown, HelpCircle, UserCircle2, Download, Pencil, Check, X,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { TimeSelect } from "@/components/TimeSelect";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  startEpoch, nextStartEpoch, fmtEpoch, accelFactor, fmtGain, realMinPerGaimHour, allTimezones, detectTimezone,
} from "@/lib/clock";
import { useI18n } from "@/lib/i18n";
import { isLiveClockOn, startLiveClock, stopLiveClock } from "@/lib/liveClock";
import { startAlarmLoop, stopAlarm, getAlarmSound } from "@/lib/alarm";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import Planning from "@/components/Planning";
import SupportTickets from "@/components/SupportTickets";
import CoachChat from "@/components/CoachChat";
import PushSettings from "@/components/PushSettings";
import MyInvoices from "@/components/MyInvoices";
import InstallApp from "@/components/InstallApp";
import InstallBanner from "@/components/InstallBanner";
import OnboardingTour from "@/components/OnboardingTour";

const GAINS = [5, 10, 15, 20];

export default function Dashboard() {
  const { user, logout, refresh } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const tz = user.timezone || detectTimezone();

  const [settings, setSettings] = useState({ start_time: "09:00", target_gain: 10 });
  const [startOverride, setStartOverride] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [showPlanning, setShowPlanning] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [superOpen, setSuperOpen] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [pushSettingsOpen, setPushSettingsOpen] = useState(false);
  const [invoicesOpen, setInvoicesOpen] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [devices, setDevices] = useState(null); // null = loading
  const [revokingSid, setRevokingSid] = useState(null);
  const [editingSid, setEditingSid] = useState(null);
  const [editName, setEditName] = useState("");
  const [liveClockOn, setLiveClockOn] = useState(isLiveClockOn());
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("gt_onboarded")) {
      localStorage.setItem("gt_onboarded", "1");
      const tmr = setTimeout(() => setOnboardOpen(true), 600);
      return () => clearTimeout(tmr);
    }
  }, []);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({
    gender: user.profile?.gender || "",
    coach_types: user.profile?.coach_types || [],
    tone: user.profile?.tone || "",
    nickname: user.profile?.nickname || "",
    situation: user.profile?.situation || "",
    goals: user.profile?.goals || "",
    interests: user.profile?.interests || "",
    challenges: user.profile?.challenges || "",
    values: user.profile?.values || "",
    motivation: user.profile?.motivation || "",
  });

  useEffect(() => {
    if (!profileOpen) return;
    setProfileForm({
      gender: user.profile?.gender || "",
      coach_types: user.profile?.coach_types || [],
      tone: user.profile?.tone || "",
      nickname: user.profile?.nickname || "",
      situation: user.profile?.situation || "",
      goals: user.profile?.goals || "",
      interests: user.profile?.interests || "",
      challenges: user.profile?.challenges || "",
      values: user.profile?.values || "",
      motivation: user.profile?.motivation || "",
    });
  }, [profileOpen, user.profile]);

  const loadDevices = useCallback(async () => {
    setDevices(null);
    try {
      const { data } = await api.get("/auth/sessions");
      setDevices(data);
    } catch {
      setDevices([]);
    }
  }, []);

  useEffect(() => {
    if (devicesOpen) loadDevices();
  }, [devicesOpen, loadDevices]);

  const revokeDevice = async (sid, isCurrent) => {
    setRevokingSid(sid);
    try {
      await api.delete(`/auth/sessions/${sid}`);
      if (isCurrent) { await logout(); navigate("/"); return; }
      await loadDevices();
    } catch {
      /* ignore */
    } finally {
      setRevokingSid(null);
    }
  };

  const renameDevice = async (sid) => {
    try {
      await api.put(`/auth/sessions/${sid}`, { name: editName.trim() });
      setEditingSid(null);
      await loadDevices();
    } catch {
      toast.error(t("err_generic"));
    }
  };

  const clockLabels = { gaim: t("lc_gaim"), gain: t("lc_gain") };

  useEffect(() => {
    if (!liveClockOn) return;
    startLiveClock(settings, tz, clockLabels).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveClockOn, settings.start_time, settings.target_gain, tz]);

  const toggleLiveClock = async () => {
    if (liveClockOn) {
      await stopLiveClock();
      setLiveClockOn(false);
      toast.info(t("lc_off"));
      return;
    }
    const res = await startLiveClock(settings, tz, clockLabels);
    if (res.ok) {
      setLiveClockOn(true);
      toast.success(t("lc_on"));
    } else {
      toast.error(res.reason === "unsupported" ? t("lc_unsupported") : t("lc_denied"));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/settings/${today}`);
        setSettings({ start_time: data.start_time || "09:00", target_gain: data.target_gain || 10 });
        if (data.reset_at) setStartOverride(new Date(data.reset_at).getTime());
      } catch { /* ignore */ }
    })();
  }, [today]);

  useEffect(() => {
    let raf;
    const loop = () => { setNowMs(Date.now()); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // notifications
  useEffect(() => {
    (async () => {
      try {
        const m = await import("@/lib/push");
        if (m.pushSupported()) setPushOn(await m.isPushEnabled());
      } catch { /* ignore */ }
    })();
  }, []);

  const targetGain = settings.target_gain;
  const factor = accelFactor(targetGain);
  const startEp = startOverride ?? startEpoch(settings.start_time, tz);
  const notStarted = nowMs < startEp;
  const elapsed = Math.max(0, nowMs - startEp);
  // Before the programmed start: GaimTime mirrors real time, gain stays 0.
  // From the start onward: GaimTime accelerates and gain rises.
  const gaimEp = notStarted ? nowMs : startEp + elapsed * factor;
  const realStr = fmtEpoch(nowMs, tz);
  const gaimStr = fmtEpoch(gaimEp, tz);
  const gain = fmtGain(elapsed * (factor - 1));
  const progress = Math.min(100, (elapsed / (8 * 3600 * 1000)) * 100);

  // Automatic alarm + notification the moment the GaimTime day starts.
  const started = nowMs >= startEp;
  const prevStartedRef = useRef(null);
  useEffect(() => {
    if (prevStartedRef.current === false && started === true) {
      startAlarmLoop(getAlarmSound());
      const title = t("daystart_title");
      const body = t("daystart_body");
      toast(title, {
        duration: Infinity,
        description: body,
        action: { label: t("pv_stop"), onClick: () => stopAlarm() },
        onDismiss: () => stopAlarm(),
      });
      try {
        if ("Notification" in window && Notification.permission === "granted" && navigator.serviceWorker) {
          navigator.serviceWorker.ready.then((reg) =>
            reg.showNotification(title, { body, icon: "/icon-192.png", badge: "/icon-192.png" })
          );
        }
      } catch { /* ignore */ }
    }
    prevStartedRef.current = started;
  }, [started, t]);

  const saveSettings = useCallback(async (target_gain, start_time) => {
    const startChanged = start_time !== settings.start_time;
    setSettings({ target_gain, start_time });
    if (startChanged) setStartOverride(null);
    try {
      await api.post("/settings", { target_gain, start_time, date: today, clear_reset: startChanged });
    } catch { toast.error("Error"); }
  }, [today, settings.start_time]);

  const changeTimezone = async (newTz) => {
    try {
      await api.put("/auth/timezone", { timezone: newTz });
      await refresh();
      toast.success(t("tz_saved"));
    } catch { toast.error("Error"); }
  };

  const chartData = useMemo(() => Array.from({ length: 9 }, (_, h) => ({ h: `${h}h`, gain: Math.round(h * targetGain) })), [targetGain]);

  const resetPrincipal = async () => {    try {
      const resetAtMs = nextStartEpoch(settings.start_time, tz);
      const { data } = await api.post("/settings/reset", { date: today, reset_at: new Date(resetAtMs).toISOString() });
      setStartOverride(new Date(data.reset_at).getTime());
      toast.success(t("reset_counters"));
    } catch { toast.error(t("err_generic")); }
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await api.put("/auth/profile", { profile: profileForm });
      await refresh();
      toast.success(t("profile_saved"));
      setProfileOpen(false);
    } catch { toast.error(t("err_generic")); }
    finally { setProfileSaving(false); }
  };

  const handleCoachClick = () => { if (user.is_coach_active) setCoachOpen(true); else setSubOpen(true); };

  const subscribeCoach = async () => {
    setSubLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { type: "coach", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error("Error"); setSubLoading(false); }
  };

  const subscribeSuper = async () => {
    setSubLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { type: "super", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error("Error"); setSubLoading(false); }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      const m = await import("@/lib/push");
      if (pushOn) { await m.disablePush(); setPushOn(false); toast.success(t("notif_off")); }
      else { await m.enablePush(); setPushOn(true); toast.success(t("notif_on")); }
    } catch (e) { toast.error(e.message || "Error"); }
    finally { setPushBusy(false); }
  };

  const submitChangePassword = async () => {
    if (pwdForm.next.length < 6) { toast.error(t("chpwd_err_short")); return; }
    if (pwdForm.next !== pwdForm.confirm) { toast.error(t("chpwd_err_match")); return; }
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwdForm.current, new_password: pwdForm.next });
      toast.success(t("chpwd_success"));
      setPwdForm({ current: "", next: "", confirm: "" });
      setPwdOpen(false);
    } catch (e) {
      const code = e?.response?.data?.detail;
      const map = { current_password_incorrect: t("chpwd_err_current"), password_too_short: t("chpwd_err_short"), password_same: t("chpwd_err_same") };
      toast.error(map[code] || t("err_generic"));
    } finally { setPwdSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grain">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/5" style={{ paddingTop: "env(safe-area-inset-top)", paddingLeft: "env(safe-area-inset-left)", paddingRight: "env(safe-area-inset-right)" }}>
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-2">
          <div className="font-display text-xl font-extrabold shrink-0">Gaim<span className="text-[#007BFF]">Time</span></div>
          <div className="flex items-center gap-1 sm:gap-2 min-w-0">
            <LanguageSwitcher compact />
            <div className="hidden sm:flex items-center gap-1.5 bg-[#00E676]/10 border border-[#00E676]/30 rounded-full px-3 py-1.5">
              <span className="text-[10px] uppercase tracking-widest text-[#00E676]/70">{t("gain")}</span>
              <span className="font-mono-clock text-sm font-bold text-[#00E676]" data-testid="header-gain">+{gain.str}</span>
            </div>
            {user.is_admin && (
              <Button data-testid="nav-admin-btn" onClick={() => navigate("/admin")} variant="outline" className="border-white/10 rounded-[12px] text-white hover:bg-white/5 px-2.5 sm:px-4 shrink-0">
                <ShieldCheck size={16} className="sm:mr-1.5" /> <span className="hidden sm:inline">{t("nav_admin")}</span>
              </Button>
            )}
            {user.is_coach_active && user.coach_tier === "super" && (
              <span data-testid="super-ia-badge" className="hidden sm:flex items-center gap-1 bg-gradient-to-r from-[#00E676]/20 to-[#007BFF]/20 border border-[#00E676]/40 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold text-[#00E676]">
                <Zap size={12} /> Super IA
              </span>
            )}
            {user.is_coach_active && user.coach_tier !== "super" && (
              <Button data-testid="upgrade-super-btn" onClick={() => setSuperOpen(true)} variant="outline"
                className="border-[#00E676]/50 text-[#00E676] hover:bg-[#00E676]/10 rounded-[12px] h-9 text-xs font-semibold px-2 sm:px-4 shrink-0">
                <Zap size={14} className="mr-1" /> Super IA
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="account-menu-btn" variant="ghost" className="rounded-[12px] gap-1.5 pl-1.5 pr-2 text-white/80 hover:text-white hover:bg-white/5 shrink-0">
                  <span className="w-8 h-8 rounded-full bg-[#007BFF]/20 border border-[#007BFF]/40 flex items-center justify-center text-xs font-bold text-[#007BFF]">
                    {(user.first_name || user.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <ChevronDown size={15} className="opacity-60 hidden sm:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#141414] border border-white/10 text-white w-64 rounded-[14px] p-1.5" data-testid="account-menu-content">
                <DropdownMenuLabel className="text-white/40 text-[11px] uppercase tracking-widest px-2">{t("menu_account")}</DropdownMenuLabel>
                <div className="px-2 pb-2" data-testid="account-info">
                  <div className="flex items-center gap-2.5">
                    <span className="w-9 h-9 rounded-full bg-[#007BFF]/20 border border-[#007BFF]/40 flex items-center justify-center text-sm font-bold text-[#007BFF] shrink-0">
                      {(user.first_name || user.email || "?").slice(0, 1).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      {(user.first_name || user.last_name) && (
                        <div className="text-sm font-semibold text-white truncate" data-testid="account-name">
                          {[user.first_name, user.last_name].filter(Boolean).join(" ")}
                        </div>
                      )}
                      <div className="text-xs text-white/50 truncate" data-testid="account-email">{user.email}</div>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {user.is_admin && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-white/10 text-white/70">Admin</span>}
                    {user.is_coach_active && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#00E676]/15 text-[#00E676]">{user.coach_tier === "super" ? "Super IA" : "Coach IA"}</span>}
                    {user.is_paid && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#007BFF]/15 text-[#007BFF]">{t("lifetime_badge")}</span>}
                  </div>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />

                <DropdownMenuItem data-testid="menu-push-toggle" onClick={togglePush} disabled={pushBusy}
                  className="gap-2.5 rounded-[10px] py-2.5 cursor-pointer focus:bg-white/5">
                  {pushBusy ? <Loader2 className="animate-spin" size={17} /> : pushOn ? <Bell size={17} className="text-[#00E676]" /> : <BellOff size={17} className="text-white/60" />}
                  <span className="flex-1">{t("menu_notif")}</span>
                  <span className={`text-[10px] uppercase tracking-wide ${pushOn ? "text-[#00E676]" : "text-white/40"}`}>{pushOn ? t("state_on") : t("state_off")}</span>
                </DropdownMenuItem>

                <DropdownMenuItem data-testid="menu-push-settings" onClick={() => setPushSettingsOpen(true)}
                  className="gap-2.5 rounded-[10px] py-2.5 cursor-pointer focus:bg-white/5">
                  <Settings size={17} className="text-white/60" /> <span className="flex-1">{t("notif_settings")}</span>
                </DropdownMenuItem>

                {user.is_coach_active && (
                  <DropdownMenuItem data-testid="menu-coach-config" onClick={() => setProfileOpen(true)}
                    className="gap-2.5 rounded-[10px] py-2.5 cursor-pointer focus:bg-white/5">
                    <Sparkles size={17} className="text-[#00E676]" /> <span className="flex-1">{t("coach_cfg_btn")}</span>
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem data-testid="menu-live-clock" onClick={toggleLiveClock}
                  className="gap-2.5 rounded-[10px] py-2.5 cursor-pointer focus:bg-white/5">
                  <Clock size={17} className={liveClockOn ? "text-[#00E676]" : "text-white/60"} />
                  <span className="flex-1">{t("lc_title")}</span>
                  <span className={`text-[10px] uppercase tracking-wide ${liveClockOn ? "text-[#00E676]" : "text-white/40"}`}>{liveClockOn ? t("state_on") : t("state_off")}</span>
                </DropdownMenuItem>

                <DropdownMenuItem data-testid="menu-devices" onClick={() => setDevicesOpen(true)}
                  className="gap-2.5 rounded-[10px] py-2.5 cursor-pointer focus:bg-white/5">
                  <MonitorSmartphone size={17} className="text-white/60" /> <span className="flex-1">{t("devices_title")}</span>
                </DropdownMenuItem>

                <DropdownMenuItem data-testid="menu-change-password" onClick={() => setPwdOpen(true)}
                  className="gap-2.5 rounded-[10px] py-2.5 cursor-pointer focus:bg-white/5">
                  <Lock size={17} className="text-white/60" /> <span className="flex-1">{t("chpwd_menu")}</span>
                </DropdownMenuItem>

                <DropdownMenuItem data-testid="menu-install-app" onClick={() => setInstallOpen(true)}
                  className="gap-2.5 rounded-[10px] py-2.5 cursor-pointer focus:bg-white/5">
                  <Download size={17} className="text-[#00E676]" /> <span className="flex-1">{t("install_app")}</span>
                </DropdownMenuItem>

                {(user.is_paid || user.is_coach_active) && (
                  <DropdownMenuItem data-testid="menu-invoices" onClick={() => setInvoicesOpen(true)}
                    className="gap-2.5 rounded-[10px] py-2.5 cursor-pointer focus:bg-white/5">
                    <Receipt size={17} className="text-white/60" /> <span className="flex-1">{t("my_invoices")}</span>
                  </DropdownMenuItem>
                )}

                {user.is_coach_active && (
                  <DropdownMenuItem data-testid="menu-support" onClick={() => setSupportOpen(true)}
                    className="gap-2.5 rounded-[10px] py-2.5 cursor-pointer focus:bg-white/5">
                    <LifeBuoy size={17} className="text-white/60" /> <span className="flex-1">{t("support_btn")}</span>
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem data-testid="menu-help" onClick={() => setOnboardOpen(true)}
                  className="gap-2.5 rounded-[10px] py-2.5 cursor-pointer focus:bg-white/5">
                  <HelpCircle size={17} className="text-white/60" /> <span className="flex-1">{t("menu_help")}</span>
                </DropdownMenuItem>

                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }}
                  className="gap-2.5 rounded-[10px] py-2.5 cursor-pointer text-[#E5484D] focus:bg-[#E5484D]/10 focus:text-[#E5484D]">
                  <LogOut size={17} /> <span className="flex-1">{t("nav_logout")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-6 md:py-8 space-y-6">
        <div className="gt-card p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Gauge className="text-[#007BFF]" size={20} />
              <div>
                <div className="text-sm font-semibold">{t("objective_t")}</div>
                <div className="text-sm font-semibold text-[#00E676]">{t("objective_d")}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-testid="ratio-selector">
              {GAINS.map((g) => (
                <button key={g} data-testid={`ratio-${g}`} onClick={() => saveSettings(g, settings.start_time)}
                  className={`px-4 py-2 rounded-[12px] font-mono-clock text-sm font-semibold transition-all ${
                    settings.target_gain === g ? "bg-[#007BFF] text-white gt-glow-blue scale-105" : "bg-[#0A0A0A] text-white/50 border border-white/10 hover:border-white/25"
                  }`}>{g} {t("minh")}</button>
              ))}
              <div className="flex items-center gap-2 ml-2 pl-2 border-l border-white/10">
                <span className="text-xs text-white/40">{t("start")}</span>
                <TimeSelect testId="start-time" value={settings.start_time}
                  onChange={(v) => saveSettings(settings.target_gain, v)} className="mt-0 w-[130px]" />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/5">
            <Globe2 className="text-[#00E676]" size={16} />
            <span className="text-xs text-white/40">{t("timezone")}</span>
            <select data-testid="timezone-select" value={tz} onChange={(e) => changeTimezone(e.target.value)}
              className="bg-[#0A0A0A] border border-white/10 rounded-[12px] px-2 py-1.5 text-sm text-white focus:border-[#007BFF] outline-none max-w-[240px]">
              {allTimezones().map((z) => <option key={z} value={z} className="bg-[#0A0A0A]">{z}</option>)}
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="gt-card p-8 relative overflow-hidden order-1 md:order-none">
            <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#007BFF]/15 blur-3xl" />
            <div className="text-xs uppercase tracking-widest text-[#007BFF]/70 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#007BFF] gt-live-dot" /> {t("gaim_clock")}
            </div>
            <div data-testid="clock-gaim" className="font-mono-clock text-6xl md:text-7xl font-bold text-[#007BFF] text-glow-blue">{gaimStr}</div>
            <div className="text-xs text-white/30 mt-2 font-mono-clock">{t("conv", { n: realMinPerGaimHour(targetGain) })}</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="gt-card p-8 relative overflow-hidden order-2 md:order-none">
            <div className="text-xs uppercase tracking-widest text-white/40 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-white/40 gt-live-dot" /> {t("real_clock")}
            </div>
            <div data-testid="clock-real" className="font-mono-clock text-4xl md:text-5xl font-bold text-white/80">{realStr}</div>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="gt-card p-6 flex flex-col justify-between">
            <div className="text-xs uppercase tracking-widest text-white/40 mb-2">{t("gain_cumule")}</div>
            <div data-testid="gain-counter" className="font-mono-clock text-4xl font-bold text-[#00E676] text-glow-mint">+{gain.str}</div>
            <div className="mt-4">
              <div className="flex justify-between text-xs text-white/40 mb-1">
                <span>{t("day8")}</span><span>{Math.round(progress)}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-white/5 overflow-hidden" data-testid="progress-bar">
                <motion.div className="h-full bg-gradient-to-r from-[#007BFF] to-[#00E676] rounded-full" animate={{ width: `${progress}%` }} transition={{ ease: "linear" }} />
              </div>
            </div>
            <Button data-testid="reset-principal-btn" onClick={resetPrincipal} variant="outline" className="mt-5 border-white/10 text-white hover:bg-white/5 rounded-[12px]">
              <RotateCcw size={15} className="mr-1.5" /> {t("reset_counters")}
            </Button>
          </div>

          <div className="gt-card p-6 md:col-span-2">
            <div className="text-xs uppercase tracking-widest text-white/40 mb-4">{t("traj_t")}</div>
            {!coachOpen && (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <XAxis dataKey="h" stroke="#A0A0A0" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#A0A0A0" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} formatter={(v) => [`${v} min`, t("gain")]} />
                <Line type="monotone" dataKey="gain" stroke="#007BFF" strokeWidth={3} dot={false} style={{ filter: "drop-shadow(0 0 8px rgba(0,123,255,0.6))" }} />
              </LineChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button data-testid="toggle-planning-btn" onClick={() => setShowPlanning((s) => !s)}
            className="bg-[#111111] border border-white/10 hover:border-[#007BFF]/50 text-white rounded-[12px] h-12 flex-1 transition-all">
            <CalendarDays size={18} className="mr-2" /> {showPlanning ? t("hide_planning") : t("open_planning")}
          </Button>
          <Button data-testid="coach-open-btn" onClick={handleCoachClick}
            className={`rounded-[12px] h-12 flex-1 transition-all ${user.is_coach_active ? "bg-[#00E676] hover:bg-[#00c765] text-black gt-glow-mint" : "bg-[#111111] border border-white/10 text-white/60 hover:border-white/25"}`}>
            {user.is_coach_active ? <MessageCircle size={18} className="mr-2" /> : <Lock size={16} className="mr-2" />}
            {t("coach_btn")}
          </Button>
        </div>

        {showPlanning && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <Planning settings={settings} date={today} tz={tz} />
          </motion.div>
        )}
      </main>

      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
              <MessageCircle className="text-[#00E676]" size={22} /> {t("coach_title")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm">{t("coachdlg_desc")}</p>

          <div className="space-y-3 mt-2">
            <div className="rounded-[14px] border border-white/10 p-4 bg-[#0A0A0A]">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{t("plan_standard")}</span>
                <span className="font-mono-clock text-2xl font-bold">9,99 €<span className="text-xs text-white/40">{t("permonth")}</span></span>
              </div>
              <p className="text-white/40 text-xs mt-1">{t("plan_standard_desc")}</p>
              <Button data-testid="coach-subscribe-btn" onClick={subscribeCoach} disabled={subLoading}
                className="w-full mt-3 bg-[#00E676] hover:bg-[#00c765] text-black rounded-[12px] h-11 font-semibold transition-all hover:scale-[1.02]">
                {subLoading ? <Loader2 className="animate-spin" /> : t("activate_coach")}
              </Button>
            </div>
            <div className="rounded-[14px] border border-[#00E676]/40 p-4 bg-gradient-to-br from-[#00E676]/10 to-[#007BFF]/10 relative">
              <span className="absolute -top-2 right-3 bg-[#00E676] text-black text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full">{t("plan_super_tag")}</span>
              <div className="flex items-center justify-between">
                <span className="font-semibold flex items-center gap-1"><Zap size={15} className="text-[#00E676]" /> {t("plan_super")}</span>
                <span className="font-mono-clock text-2xl font-bold">19,99 €<span className="text-xs text-white/40">{t("permonth")}</span></span>
              </div>
              <p className="text-white/50 text-xs mt-1">{t("plan_super_desc")}</p>
              <Button data-testid="super-subscribe-btn" onClick={subscribeSuper} disabled={subLoading}
                className="w-full mt-3 bg-white text-black hover:bg-white/90 rounded-[12px] h-11 font-semibold transition-all hover:scale-[1.02]">
                {subLoading ? <Loader2 className="animate-spin" /> : t("activate_super")}
              </Button>
            </div>
          </div>
          <p className="text-center text-xs text-white/40 mt-1">{t("secure_pay")}</p>
        </DialogContent>
      </Dialog>

      <Dialog open={superOpen} onOpenChange={setSuperOpen}>
        <DialogContent className="bg-[#111111] border border-[#00E676]/30 text-white rounded-[16px] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
              <Zap className="text-[#00E676]" size={22} /> {t("plan_super")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm">{t("plan_super_desc")}</p>
          <div className="flex items-baseline gap-2 my-3">
            <span className="font-mono-clock text-4xl font-bold">19,99 €</span>
            <span className="text-white/40">{t("permonth")}</span>
          </div>
          <Button data-testid="super-upgrade-confirm-btn" onClick={subscribeSuper} disabled={subLoading}
            className="w-full bg-[#00E676] hover:bg-[#00c765] text-black rounded-[12px] h-12 font-semibold transition-all hover:scale-[1.02]">
            {subLoading ? <Loader2 className="animate-spin" /> : t("activate_super")}
          </Button>
          <p className="text-center text-xs text-white/40">{t("secure_pay")}</p>
        </DialogContent>
      </Dialog>

      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold flex items-center gap-2">
              <Lock className="text-[#007BFF]" size={20} /> {t("chpwd_title")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs text-white/50 mb-1 block">{t("chpwd_current")}</label>
              <input data-testid="chpwd-current" type="password" value={pwdForm.current} autoComplete="current-password"
                onChange={(e) => setPwdForm((f) => ({ ...f, current: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-[12px] px-3 py-2.5 text-sm text-white focus:border-[#007BFF] outline-none" />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">{t("chpwd_new")}</label>
              <input data-testid="chpwd-new" type="password" value={pwdForm.next} autoComplete="new-password"
                onChange={(e) => setPwdForm((f) => ({ ...f, next: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-[12px] px-3 py-2.5 text-sm text-white focus:border-[#007BFF] outline-none" />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">{t("chpwd_confirm")}</label>
              <input data-testid="chpwd-confirm" type="password" value={pwdForm.confirm} autoComplete="new-password"
                onChange={(e) => setPwdForm((f) => ({ ...f, confirm: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-[12px] px-3 py-2.5 text-sm text-white focus:border-[#007BFF] outline-none" />
            </div>
            <Button data-testid="chpwd-submit" onClick={submitChangePassword} disabled={pwdSaving || !pwdForm.current || !pwdForm.next}
              className="w-full bg-[#007BFF] hover:bg-[#0066d6] text-white rounded-[12px] h-11 font-semibold transition-all hover:scale-[1.02]">
              {pwdSaving ? <Loader2 className="animate-spin" /> : t("chpwd_save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <CoachChat open={coachOpen} onClose={() => setCoachOpen(false)} />
      <SupportTickets open={supportOpen} onClose={() => setSupportOpen(false)} />
      <PushSettings open={pushSettingsOpen} onClose={() => setPushSettingsOpen(false)} />
      <OnboardingTour open={onboardOpen} onClose={() => setOnboardOpen(false)} />
      <InstallApp open={installOpen} onClose={() => setInstallOpen(false)} />
      <InstallBanner onInstall={() => setInstallOpen(true)} />

      <Dialog open={devicesOpen} onOpenChange={setDevicesOpen}>
        <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-md" data-testid="devices-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold flex items-center gap-2">
              <MonitorSmartphone className="text-[#007BFF]" size={20} /> {t("devices_title")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-white/50 text-sm -mt-1">{t("devices_desc")}</p>
          <div className="space-y-2 mt-2">
            {devices === null ? (
              <div className="flex justify-center py-6"><Loader2 className="animate-spin text-white/40" size={22} /></div>
            ) : devices.length === 0 ? (
              <p className="text-white/40 text-sm py-4 text-center">{t("devices_empty")}</p>
            ) : (
              devices.map((s) => (
                <div key={s.sid} data-testid="device-item" className="flex items-center gap-3 rounded-[12px] border border-white/10 bg-[#0A0A0A] px-3 py-3">
                  <MonitorSmartphone size={18} className={s.current ? "text-[#00E676] shrink-0" : "text-[#007BFF] shrink-0"} />
                  <div className="flex-1 min-w-0">
                    {editingSid === s.sid ? (
                      <div className="flex items-center gap-2">
                        <Input data-testid="device-name-input" value={editName} maxLength={40} autoFocus
                          onChange={(e) => setEditName(e.target.value)} placeholder={t("dev_rename_ph")}
                          onKeyDown={(e) => e.key === "Enter" && renameDevice(s.sid)}
                          className="bg-[#111] border-white/10 rounded-[8px] h-8 text-sm" />
                        <button data-testid="device-name-save" onClick={() => renameDevice(s.sid)} className="text-[#00E676] hover:scale-110 transition-transform shrink-0"><Check size={16} /></button>
                        <button data-testid="device-name-cancel" onClick={() => setEditingSid(null)} className="text-white/40 hover:text-white shrink-0"><X size={16} /></button>
                      </div>
                    ) : (
                      <>
                        <div className="text-sm font-medium truncate flex items-center gap-2">
                          <span className="truncate">{s.name || s.device}</span>
                          {s.current && <span className="text-[10px] uppercase tracking-wide text-[#00E676] border border-[#00E676]/40 rounded-full px-2 py-0.5 shrink-0">{t("dev_this_device")}</span>}
                          <button data-testid="device-rename-btn" onClick={() => { setEditName(s.custom ? s.name : ""); setEditingSid(s.sid); }} title={t("dev_rename")} className="text-white/30 hover:text-[#007BFF] transition-colors shrink-0"><Pencil size={13} /></button>
                        </div>
                        {s.last_seen && <div className="text-white/40 text-xs mt-0.5">{t("dev_seen")}: {new Date(s.last_seen).toLocaleString()}</div>}
                      </>
                    )}
                  </div>
                  <Button data-testid="device-revoke-btn" onClick={() => revokeDevice(s.sid, s.current)} disabled={revokingSid !== null || editingSid === s.sid}
                    className="bg-[#E5484D] hover:bg-[#c93b40] rounded-[10px] h-9 px-3 text-sm shrink-0">
                    {revokingSid === s.sid ? <Loader2 className="animate-spin" size={16} /> : t("dev_disconnect")}
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      <MyInvoices open={invoicesOpen} onClose={() => setInvoicesOpen(false)} />

      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
              <Sparkles className="text-[#00E676]" size={22} /> {t("coach_cfg_title")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-white/50 text-sm -mt-1">{t("coach_cfg_desc")}</p>

          <div className="space-y-4 mt-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/40 mb-2">{t("cfg_gender")}</div>
              <div className="flex gap-2">
                {[["homme", t("cfg_gender_m")], ["femme", t("cfg_gender_f")]].map(([val, label]) => (
                  <button key={val} type="button" data-testid={`cfg-gender-${val}`}
                    onClick={() => setProfileForm((f) => ({ ...f, gender: f.gender === val ? "" : val }))}
                    className={`flex-1 py-2 rounded-[12px] text-sm font-semibold border transition-all ${profileForm.gender === val ? "bg-[#007BFF] border-[#007BFF] text-white" : "bg-[#0A0A0A] border-white/10 text-white/50 hover:border-white/25"}`}>{label}</button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-white/40 mb-2">{t("cfg_type")}</div>
              <div className="flex flex-wrap gap-2">
                {[["work", t("type_work")], ["life", t("type_life")], ["love", t("type_love")], ["intimate", t("type_intimate")], ["company", t("type_company")], ["other", t("type_other")]].map(([val, label]) => {
                  const active = (profileForm.coach_types || []).includes(val);
                  return (
                    <button key={val} type="button" data-testid={`cfg-type-${val}`}
                      onClick={() => setProfileForm((f) => ({ ...f, coach_types: active ? (f.coach_types || []).filter((x) => x !== val) : [...(f.coach_types || []), val] }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${active ? "bg-[#00E676]/15 border-[#00E676]/50 text-[#00E676]" : "bg-[#0A0A0A] border-white/10 text-white/50 hover:border-white/25"}`}>{label}</button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-white/40 mb-2">{t("cfg_tone")}</div>
              <div className="flex gap-2">
                {[["direct", t("tone_direct")], ["doux", t("tone_soft")], ["motivant", t("tone_motiv")]].map(([val, label]) => (
                  <button key={val} type="button" data-testid={`cfg-tone-${val}`}
                    onClick={() => setProfileForm((f) => ({ ...f, tone: f.tone === val ? "" : val }))}
                    className={`flex-1 py-2 rounded-[12px] text-xs font-semibold border transition-all ${profileForm.tone === val ? "bg-[#007BFF] border-[#007BFF] text-white" : "bg-[#0A0A0A] border-white/10 text-white/50 hover:border-white/25"}`}>{label}</button>
                ))}
              </div>
            </div>

            <Input data-testid="cfg-nickname" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 rounded-[12px] h-11 focus-visible:ring-[#007BFF]"
              placeholder={t("cfg_nickname")} value={profileForm.nickname} onChange={(e) => setProfileForm((f) => ({ ...f, nickname: e.target.value }))} />
            <Textarea data-testid="profile-situation" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 rounded-[12px] min-h-[44px] py-2 focus-visible:ring-[#007BFF]"
              placeholder={t("situation_ph")} value={profileForm.situation} onChange={(e) => setProfileForm((f) => ({ ...f, situation: e.target.value }))} />
            <Textarea data-testid="profile-goals" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 rounded-[12px] min-h-[44px] py-2 focus-visible:ring-[#007BFF]"
              placeholder={t("goals_ph")} value={profileForm.goals} onChange={(e) => setProfileForm((f) => ({ ...f, goals: e.target.value }))} />
            <Textarea data-testid="profile-interests" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 rounded-[12px] min-h-[44px] py-2 focus-visible:ring-[#007BFF]"
              placeholder={t("interests_ph")} value={profileForm.interests} onChange={(e) => setProfileForm((f) => ({ ...f, interests: e.target.value }))} />
            <Textarea data-testid="profile-challenges" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 rounded-[12px] min-h-[44px] py-2 focus-visible:ring-[#007BFF]"
              placeholder={t("cfg_challenges")} value={profileForm.challenges} onChange={(e) => setProfileForm((f) => ({ ...f, challenges: e.target.value }))} />
            <Textarea data-testid="profile-values" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 rounded-[12px] min-h-[44px] py-2 focus-visible:ring-[#007BFF]"
              placeholder={t("cfg_values")} value={profileForm.values} onChange={(e) => setProfileForm((f) => ({ ...f, values: e.target.value }))} />
            <Textarea data-testid="profile-motivation" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 rounded-[12px] min-h-[44px] py-2 focus-visible:ring-[#007BFF]"
              placeholder={t("cfg_motivation")} value={profileForm.motivation} onChange={(e) => setProfileForm((f) => ({ ...f, motivation: e.target.value }))} />
          </div>

          <div className="flex gap-3 mt-3">
            <Button data-testid="profile-cancel-btn" onClick={() => setProfileOpen(false)} variant="outline" className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-[12px]">
              {t("cancel")}
            </Button>
            <Button data-testid="profile-save-btn" onClick={saveProfile} disabled={profileSaving} className="flex-1 bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] font-semibold">
              {profileSaving ? <Loader2 className="animate-spin" /> : t("save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

